import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export type AsyncState<T> =
  | { status: "loading"; data: undefined; error: undefined }
  | { status: "success"; data: T; error: undefined }
  | { status: "error"; data: undefined; error: Error };

type Settled<T> = { run: string } & (
  | { status: "success"; data: T; error: undefined }
  | { status: "error"; data: undefined; error: Error }
);

const LOADING = { status: "loading", data: undefined, error: undefined } as const;

/**
 * Run `load` whenever `key` changes, aborting the previous run. `reload()` re-runs it.
 * Errors are surfaced, never swallowed: callers must render the error state.
 */
export function useAsync<T>(
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
): AsyncState<T> & { reload: () => void } {
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<Settled<T> | null>(null);
  const run = `${attempt}:${key}`;

  // Callers pass inline closures; re-run on `key`, not on closure identity.
  const loadRef = useRef(load);
  useLayoutEffect(() => {
    loadRef.current = load;
  });

  useEffect(() => {
    const controller = new AbortController();
    loadRef.current(controller.signal).then(
      (data) => {
        if (!controller.signal.aborted)
          setSettled({ run, status: "success", data, error: undefined });
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        const err = error instanceof Error ? error : new Error(String(error));
        setSettled({ run, status: "error", data: undefined, error: err });
      },
    );
    return () => controller.abort();
  }, [run]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  if (!settled || settled.run !== run) return { ...LOADING, reload };
  const { run: _run, ...state } = settled;
  return { ...state, reload };
}
